import { Button } from "@shared/ui/button";
import { Label } from "@shared/ui/label";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@shared/ui/select";
import React, { useEffect, useMemo, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useCategoryOptions, useAccountStatus } from "../../hooks";
import { ConsumerFilters } from "../../types";
import { mapToLabelValue } from "@shared/utils/mapToLabelValue";

interface FilterPanelProps {
  currentFilters: ConsumerFilters;
  onApplyFilters: (filters: Partial<ConsumerFilters>) => void;
  onResetFilters: () => void;
}

export const FilterPanel: React.FC<FilterPanelProps> = ({
  currentFilters,
  onApplyFilters,
  onResetFilters,
}) => {
  // Fetch categories and statuses
  const { data: categories } = useCategoryOptions(
    Number(currentFilters.remoteUtilityId)
  );
  const { data: statuses } = useAccountStatus();
    const statusOptions = useMemo(
      () =>
        statuses?.result?.map((item: any) => ({
          label: item.value,
          value: item.value,
        })) || [],
      [statuses]
    );


  // Local state for temporary filter values
  const [tempFilters, setTempFilters] = useState<{
    status: string;
    category: string;
  }>({
    status: currentFilters.status || '',
    category: currentFilters.category || "ALL_CATEGORIES",
  });


  // Convert numeric status to string for form
  const selectedStatusValue =
    tempFilters.status
      ? String(tempFilters.status)
      : "ALL_STATUS";

  // Initialize form with current filter values
  const form = useForm({
    defaultValues: {
      status: selectedStatusValue || "",
      category: tempFilters.category || "ALL_CATEGORIES",
    },
  });

  // Update temp filters when current filters change (e.g., from URL)
  useEffect(() => {
    const newTempFilters = {
      status: currentFilters.status || '',
      category: currentFilters.category || "ALL_CATEGORIES",
    };

    setTempFilters(newTempFilters);

    // Update form values
    const newSelectedStatusValue =
      newTempFilters.status
        ? String(newTempFilters.status)
        : "ALL_STATUS";

    form.setValue("status", newSelectedStatusValue);
    form.setValue("category", newTempFilters.category);
  }, [currentFilters, form]);

  // Update form when tempFilters change
  useEffect(() => {
    const newSelectedStatusValue =
      tempFilters.status
        ? String(tempFilters.status)
        : "ALL_STATUS";

    form.setValue("status", newSelectedStatusValue);
    form.setValue("category", tempFilters.category);
  }, [tempFilters, form]);

  const handleStatusChange = (value: string) => {
    setTempFilters((prev) => ({
      ...prev,
      status: value === "ALL_STATUS" ? '' : value,
    }));
  };

  const handleCategoryChange = (value: string) => {
    setTempFilters((prev) => ({
      ...prev,
      category: value === "ALL_CATEGORIES" ? "" : value,
    }));
  };

  const handleApplyFilters = () => {
    const filtersToApply: Partial<ConsumerFilters> = {};

    if (tempFilters.status) {
      filtersToApply.status = tempFilters.status;
    }
    if (tempFilters.category && tempFilters.category !== "ALL_CATEGORIES") {
      filtersToApply.category = tempFilters.category;
    }

    onApplyFilters(filtersToApply);
  };

  const handleResetFilters = () => {
    setTempFilters({
      status:'',
      category: "ALL_CATEGORIES",
    });
    onResetFilters();
  };

  const handleSubmit = (data: any) => {
    handleApplyFilters();
  };

  return (
    <form onSubmit={form.handleSubmit(handleSubmit)}>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-6">
        <div className="space-y-2">
          <Label>Account Status</Label>
          <Controller
            name="status"
            control={form.control}
            render={({ field }) => (
              <Select
                value={selectedStatusValue}
                onValueChange={(value) => {
                  field.onChange(value);
                  handleStatusChange(value);
                }}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectGroup>
                    <SelectItem value="ALL_STATUS">All Status</SelectItem>
                    {statusOptions?.map((status: any) => (
                      <SelectItem key={status.value} value={String(status.value)}>
                        {status.label}
                      </SelectItem>
                    ))}
                  </SelectGroup>
                </SelectContent>
              </Select>
            )}
          />
        </div>

        <div className="space-y-2">
          <Label>Account Category</Label>
          <Controller
            name="category"
            control={form.control}
            render={({ field }) => (
              <Select
                value={tempFilters.category || "ALL_CATEGORIES"}
                onValueChange={(value) => {
                  field.onChange(value);
                  handleCategoryChange(value);
                }}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select category" />
                </SelectTrigger>
                <SelectContent>
                  <SelectGroup>
                    <SelectItem value="ALL_CATEGORIES">
                      All Categories
                    </SelectItem>
                    {categories?.result?.map((category: any) => (
                      <SelectItem key={category.code} value={category.code}>
                        {category.name}
                      </SelectItem>
                    ))}
                  </SelectGroup>
                </SelectContent>
              </Select>
            )}
          />
        </div>

        <div className="col-span-1 md:col-span-2 lg:col-span-2 flex justify-end gap-2">
          <Button type="button" variant="outline" onClick={handleResetFilters}>
            Reset Filters
          </Button>
          <Button type="submit">Apply Filters</Button>
        </div>
      </div>
    </form>
  );
};
